import { Fragment, useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useReconcileVans, useVanReconcile, useItemMovements } from '../hooks/useVanReconcile';

// "My stock is not right."
//
// Pick the engineer, and this shows the arithmetic behind every figure on his
// van: the week-37 opening count, then every job card, faulty return, order and
// transfer that moved it since.
//
// The numbers are produced by the same derivation the ordering screen uses —
// verified line for line across all 165 vans — because a reconciliation that
// disagrees with the screen causing the complaint would settle nothing.

const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '');

// A van-to-van handover books a plus on one van and a minus on the other, and
// both used to read "Collected" - so the van that GAVE stock away showed
// "Collected −2". Each direction is named for what it was.
const KIND = {
  receipt: { label: 'Collected', cls: 'text-emerald-700', sign: '+' },
  transfer_in: { label: 'Received from van', cls: 'text-emerald-700', sign: '+' },
  transfer_out: { label: 'Given to van', cls: 'text-sky-700', sign: '−' },
  storeroom_in: { label: 'From storeroom', cls: 'text-emerald-700', sign: '+' },
  storeroom_out: { label: 'Back to storeroom', cls: 'text-sky-700', sign: '−' },
  adjustment: { label: 'Adjustment', cls: 'text-amber-700', sign: '' },
  usage: { label: 'Used on job', cls: 'text-gray-700', sign: '−' },
  faulty: { label: 'Faulty', cls: 'text-amber-700', sign: '−' },
};
const ADDS = new Set(['receipt', 'transfer_in', 'storeroom_in', 'adjustment']);

// Page sizes offered on both pagers. 200 is the ceiling the server enforces
// on the movements endpoint, so the list cannot ask for more than it will give.
const PAGE_SIZES = [10, 20, 50, 100, 200];
const DEFAULT_MOVEMENTS = 10;
const DEFAULT_ITEMS = 25;

// Newest first, a page at a time.
//
// The running balance still works across pages: the server sends the balance
// the oldest row on THIS page starts from, so each page can be walked upward
// without the browser holding the whole history. After a month a busy line has
// hundreds of movements, and shipping them all to render twenty was never going
// to hold up.
function Movements({ registration, item, week }) {
  const [offset, setOffset] = useState(0);
  const [size, setSize] = useState(DEFAULT_MOVEMENTS);
  const { data, isLoading } = useItemMovements(registration, item.stock_item_id, { limit: size, offset });

  if (isLoading && !data) return <p className="px-4 py-3 text-xs text-gray-400">Loading movements…</p>;
  if (!data) return null;
  if (!data.total) {
    return (
      <p className="px-4 py-3 text-xs text-gray-400">
        Nothing has moved since the week {week} count of {num(data.opening)}.
      </p>
    );
  }

  // Rows arrive newest first; the balance climbs as we walk back up them.
  const rows = data.movements;
  const balances = [];
  let running = data.balance_before_page;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    running += ADDS.has(rows[i].kind) ? rows[i].qty : -rows[i].qty;
    balances[i] = running;
  }

  const from = data.offset + 1;
  const to = Math.min(data.offset + rows.length, data.total);
  const onFirst = data.offset === 0;
  const onLast = to >= data.total;
  const pageNo = Math.floor(data.offset / size) + 1;
  const pageCount = Math.max(1, Math.ceil(data.total / size));

  return (
    <div>
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
          <tr>
            <th className="px-4 py-1.5">When</th>
            <th className="px-4 py-1.5">What</th>
            <th className="px-4 py-1.5">Reference</th>
            <th className="px-4 py-1.5 text-right">Qty</th>
            <th className="px-4 py-1.5 text-right">Balance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((m, i) => {
            const k = KIND[m.kind] || { label: m.kind, cls: 'text-gray-700', sign: '' };
            return (
              <tr key={`${m.kind}-${m.at}-${i}`} className="bg-white">
                <td className="whitespace-nowrap px-4 py-1.5 text-gray-500">{fmtDate(m.at)}</td>
                <td className={`px-4 py-1.5 font-medium ${k.cls}`}>{k.label}</td>
                <td className="px-4 py-1.5 text-xs text-gray-500">
                  {m.ref || '—'}
                  {m.detail ? <span className="ml-1 text-gray-400">{String(m.detail).slice(0, 40)}</span> : null}
                </td>
                <td className={`px-4 py-1.5 text-right tabular-nums ${k.cls}`}>{k.sign}{num(m.qty)}</td>
                <td className="px-4 py-1.5 text-right font-semibold tabular-nums">{num(balances[i])}</td>
              </tr>
            );
          })}
          {/* The opening count closes the story, and only belongs on the last
              page — it is the oldest thing that happened. */}
          {onLast ? (
            <tr className="bg-white">
              <td className="px-4 py-1.5 text-gray-500" />
              <td className="px-4 py-1.5 font-medium text-gray-900">Counted</td>
              <td className="px-4 py-1.5 text-xs text-gray-500">week {week} opening balance</td>
              <td className="px-4 py-1.5 text-right tabular-nums">{num(data.opening)}</td>
              <td className="px-4 py-1.5 text-right font-semibold tabular-nums">{num(data.opening)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {/* Same Previous / n of n / Next as the item table above, so there is one
          pager to learn rather than two. Still newest first, which the caption
          says — "Next" walks back in time. */}
      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {from}–{to} of {data.total} movements, newest first
          </span>
          {/* Changing the size returns to the first page: keeping the offset
              would land somewhere arbitrary in the middle of the history. */}
          <select
            value={size}
            onChange={(e) => { setSize(Number(e.target.value)); setOffset(0); }}
            className="rounded-md border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600"
          >
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} a page</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" disabled={onFirst} onClick={() => setOffset(Math.max(offset - size, 0))}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 disabled:opacity-40">
            Previous
          </button>
          <span className="px-2 text-xs text-gray-500">{pageNo} / {pageCount}</span>
          <button type="button" disabled={onLast} onClick={() => setOffset(offset + size)}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 disabled:opacity-40">
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function StockCheck() {
  const [van, setVan] = useState('');
  const [vanSearch, setVanSearch] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [onlyMoved, setOnlyMoved] = useState(false);

  const { data: vans } = useReconcileVans();
  const { data, isLoading, isFetching, refetch } = useVanReconcile(van);

  const vanOptions = useMemo(() => {
    const needle = vanSearch.trim().toLowerCase();
    return (vans || []).filter((v) => !needle || v.registration === van
      || `${v.registration} ${v.driver_name || ''}`.toLowerCase().includes(needle));
  }, [vans, vanSearch, van]);

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.items || [])
      .filter((i) => !onlyMoved || i.movement_count > 0)
      .filter((i) => !needle || `${i.sku || ''} ${i.name || ''}`.toLowerCase().includes(needle));
  }, [data, q, onlyMoved]);

  // The item list is bounded by the catalogue rather than by time, but a van
  // still carries 120 lines and the whole point of this screen is to find one
  // of them — so it pages, and the filters reset to the first page.
  const [page, setPage] = useState(0);
  const [itemSize, setItemSize] = useState(DEFAULT_ITEMS);
  const pages = Math.max(1, Math.ceil(items.length / itemSize));
  const current = Math.min(page, pages - 1);
  const shown = items.slice(current * itemSize, (current + 1) * itemSize);
  useEffect(() => { setPage(0); setOpen(null); }, [q, onlyMoved, van, itemSize]);

  const t = data?.totals;
  const sel = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Check</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            For when an engineer says his stock is wrong. Pick him, and this shows the opening
            count and every movement since — what he collected, what he used on which job, and
            what was written off faulty.
          </p>
        </div>
        <button type="button" onClick={() => refetch()} disabled={!van || isFetching}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input value={vanSearch} onChange={(e) => setVanSearch(e.target.value)}
            placeholder="Engineer or registration…" className={`${sel} w-56 pl-8`} />
        </div>
        <select value={van} onChange={(e) => { setVan(e.target.value); setOpen(null); }} className={`${sel} max-w-sm`}>
          <option value="">{vanOptions.length ? `— choose an engineer (${vanOptions.length}) —` : '— nobody matches —'}</option>
          {vanOptions.map((v) => (
            <option key={v.registration} value={v.registration}>
              {v.driver_name ? `${v.driver_name} — ${v.registration}` : v.registration}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? <p className="mt-6 text-sm text-gray-400">Working it out…</p> : null}

      {data && !data.counted ? (
        <div className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">{data.message}</div>
      ) : null}

      {data?.counted ? (
        <>
          <p className="mt-4 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{data.driver_name || data.van_registration}</span>
            {data.driver_name ? <span className="text-gray-500"> · {data.van_registration}</span> : null}
            {' · opening balance from the week '}{data.baseline.week}{' count'}
            {data.baseline.counts.length ? (
              <span className="text-gray-500">
                {' '}({data.baseline.counts.map((c) => `${fmtDate(c.date)} by ${c.counted_by || 'unknown'}`).join(', ')})
              </span>
            ) : null}
          </p>
          {/* Every other van opens on its first count from week 37. One that
              doesn't must say so and say why, or the next person to check it
              will assume the week-37 history was simply lost. */}
          {data.baseline.override ? (
            <div className="mt-2 max-w-3xl rounded-r border-l-2 border-sky-400 bg-sky-50 px-3 py-2">
              <div className="text-xs font-semibold text-sky-900">
                Opening balance reset to week {data.baseline.week} — an exception to the week 37 rule
              </div>
              <div className="mt-0.5 text-xs text-sky-800">{data.baseline.override.reason}</div>
              <div className="mt-0.5 text-[11px] text-sky-700">
                Set by {data.baseline.override.set_by || 'unknown'} on {fmtDate(data.baseline.override.set_at)}
              </div>
            </div>
          ) : null}

          {/* The whole van in one line of arithmetic, in the same order the
              figures are worked out per item. */}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            {[
              ['opening', t.opening, 'text-gray-900'],
              ['+ collected', t.received, 'text-emerald-700'],
              ['± transferred', t.transferred, 'text-sky-700'],
              ['− used', t.used, 'text-gray-700'],
              ['− faulty', t.faulty, 'text-amber-700'],
            ].map(([label, value, cls]) => (
              <div key={label} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div className={`text-lg font-bold tabular-nums ${cls}`}>{num(value)}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </div>
            ))}
            <div className="rounded-lg border border-brand/30 bg-brand-light/30 px-3 py-2">
              <div className="text-lg font-bold tabular-nums text-gray-900">{num(t.on_hand)}</div>
              <div className="text-xs text-gray-500">= on the van now</div>
            </div>
            {t.negative_lines > 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <div className="text-lg font-bold tabular-nums text-amber-700">{t.negative_lines}</div>
                <div className="text-xs text-gray-500">below zero</div>
              </div>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an item…"
                className={`${sel} w-60 pl-8`} />
            </div>
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input type="checkbox" checked={onlyMoved} onChange={(e) => setOnlyMoved(e.target.checked)}
                className="rounded border-gray-300 text-brand focus:ring-brand" />
              Only items that have moved
            </label>
            <span className="text-xs text-gray-400">{items.length} of {data.items.length} items</span>
          </div>

          <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2.5">Item</th>
                  <th className="px-4 py-2.5 text-right">Opening</th>
                  <th className="px-4 py-2.5 text-right">Collected</th>
                  <th className="px-4 py-2.5 text-right">Transferred</th>
                  <th className="px-4 py-2.5 text-right">Used</th>
                  <th className="px-4 py-2.5 text-right">Faulty</th>
                  <th className="px-4 py-2.5 text-right">On van</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shown.map((i) => {
                  const showing = open === i.stock_item_id;
                  return (
                    <Fragment key={i.stock_item_id}>
                      <tr
                        onClick={() => setOpen(showing ? null : i.stock_item_id)}
                        className={`cursor-pointer ${i.on_hand < 0 ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-2">
                          <div className="text-gray-800">{i.name || i.sku}</div>
                          <div className="font-mono text-[11px] text-gray-400">
                            {i.sku} · {i.movement_count} movement{i.movement_count === 1 ? '' : 's'}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-600">{num(i.opening)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{i.received ? `+${num(i.received)}` : ''}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-sky-700" title="Van-to-van handovers and storeroom transfers, netted">
                          {i.transferred ? `${i.transferred > 0 ? '+' : '−'}${num(Math.abs(i.transferred))}` : ''}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">{i.used ? `−${num(i.used)}` : ''}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-amber-700">{i.faulty ? `−${num(i.faulty)}` : ''}</td>
                        <td className={`px-4 py-2 text-right font-semibold tabular-nums ${i.on_hand < 0 ? 'text-amber-700' : 'text-gray-900'}`}>
                          {num(i.on_hand)}
                          {i.on_hand < 0 ? <ExclamationTriangleIcon className="ml-1 inline h-4 w-4" /> : null}
                        </td>
                      </tr>
                      {showing ? (
                        <tr>
                          <td colSpan={7} className="bg-gray-50 p-0">
                            <Movements registration={van} item={i} week={data.baseline.week} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
                {!shown.length ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Nothing matches.</td></tr>
                ) : null}
              </tbody>
            </table>
            {items.length ? (
              <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {current * itemSize + 1}–{Math.min((current + 1) * itemSize, items.length)}
                    {' of '}{items.length} items
                  </span>
                  <select
                    value={itemSize}
                    onChange={(e) => setItemSize(Number(e.target.value))}
                    className="rounded-md border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600"
                  >
                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} a page</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)}
                    className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 disabled:opacity-40">
                    Previous
                  </button>
                  <span className="px-2 text-xs text-gray-500">{current + 1} / {pages}</span>
                  <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}
                    className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 disabled:opacity-40">
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default StockCheck;
